import { Moon, Sun } from "lucide-react";
import { ChromeIconButton, Tooltip } from "@playbookai/ui";

export interface ThemeToggleButtonProps {
  readonly darkMode: boolean;
  readonly onToggle: () => void;
}

/**
 * The top-bar theme control (PB-5176 d9), passed through `TopBar`'s
 * `themeToggleSlot`. An ACTION button named for the theme it switches to —
 * never a pressed-state toggle: the APG toggle rule says a label must not
 * change with state, and high contrast counts as dark (`darkMode` is the
 * resolved look not being light — Match device resolves from the device), so
 * a pressed "dark" state would misreport. Naming the
 * target stays truthful under high contrast because the store already takes
 * high contrast to light.
 */
export function ThemeToggleButton({
  darkMode,
  onToggle,
}: ThemeToggleButtonProps): JSX.Element {
  const label = darkMode ? "Switch to light theme" : "Switch to dark theme";
  const Icon = darkMode ? Sun : Moon;
  /* The bar's chrome control (ChromeIconButton `bar`) with the
     design-system Tooltip in place of the native title (d1). The name already says it, so the tooltip does not
     describe the button a second time. */
  return (
    <Tooltip content={label} describeChild={false}>
      <ChromeIconButton variant="bar" aria-label={label} onClick={onToggle}>
        <Icon className="h-5 w-5 stroke-[1.5]" />
      </ChromeIconButton>
    </Tooltip>
  );
}

/**
 * Holds the toggle's 32px slot while the initial preference read is pending so
 * the utility cluster does not shift on first paint (review v2 minor 5). Inert
 * and hidden from assistive tech; nothing renders once the read is unavailable.
 */
export function ThemeTogglePlaceholder(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-testid="theme-toggle-placeholder"
      className="inline-block h-8 w-8 shrink-0"
    />
  );
}
